import qs from "qs";
import swal from "sweetalert2";
import React, { useEffect, useState } from "react";
import BreadCrumbs from "../../common/breadcrumbs";
import Modal from "../../common/Modal";
import SearchBox from "../../common/searchBox";
import AddUpdate from "./addUpdate";
import {
	useGetHelpTopics,
	useUpdateHelpTopic,
	useDeleteHelpTopic,
} from "../../../queryHooks";
import SingleStatusToggle from "../../common/singleStatusToggle";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import Table from "../../common/table";
import CS from "../../../services/commonService";
import { ROLES_CONFIG } from "../../../config/configs";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";
//	Pre-defined values
const columnKey = "helpTopics";
//	Component starts
const HelpTopics = () => {
	//	State values
	const setLoading = useStore((state) => state.setLoading);
	const [topicFor, setTopicFor] = useState(ROLES_CONFIG.RESTAURANT);
	const [tabChangeCounter, setTabChangeCounter] = useState(0);
	const [helpTopic, setHelpTopic] = useState({});
	const [open, setOpen] = useState(false);
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [helpTopicsPayload, setHelpTopicsPayload] = useState({
		pagination: true,
		items_per_page: itemsPerPage,
		page: pageNow,
		filter: { ["help_topics.role_id"]: ROLES_CONFIG.RESTAURANT },
	});
	const {
		data: dataGetHelpTopic,
		isLoading,
		refetch,
	} = useGetHelpTopics(qs.stringify(helpTopicsPayload));

	useEffect(() => {
		if (dataGetHelpTopic && dataGetHelpTopic.status === "success") {
			let respData = dataGetHelpTopic.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataGetHelpTopic]);

	const {
		mutateAsync: updateHelpTopic,
		error: errorUpdateHelpTopic,
		data: dataUpdateHelpTopic,
	} = useUpdateHelpTopic();

	useEffect(() => {
		if (dataUpdateHelpTopic) {
			setLoading(false);
			if (dataUpdateHelpTopic.status === "success") {
				CS.showSuccessMessage("Status updated successfully");
				refetch();
			} else if (dataUpdateHelpTopic.status === "fail") {
				CS.showErrorMessage(dataUpdateHelpTopic.message);
			}
		}
	}, [dataUpdateHelpTopic]);

	useEffect(() => {
		if (errorUpdateHelpTopic) {
			setLoading(false);
			CS.showErrorMessage(errorUpdateHelpTopic.response?.data?.message);
		}
	}, [errorUpdateHelpTopic]);

	const {
		mutateAsync: deleteHelpTopic,
		error: errorDeleteHelpTopic,
		data: dataDeleteHelpTopic,
	} = useDeleteHelpTopic();

	useEffect(() => {
		if (dataDeleteHelpTopic) {
			setLoading(false);
			if (dataDeleteHelpTopic.status === "success") {
				let _page = pageToLoadOnDelete(
					tableData?.total_items,
					1,
					pageNow,
					itemsPerPage
				);
				if (_page === pageNow) refetch();
				setPageNow(_page);
				setHelpTopicsPayload({ ...helpTopicsPayload, page: _page });
				CS.showSuccessMessage(dataDeleteHelpTopic.message);
			} else if (dataDeleteHelpTopic.status === "fail") {
				CS.showErrorMessage(dataDeleteHelpTopic.message);
			}
		}
	}, [dataDeleteHelpTopic]);

	useEffect(() => {
		if (errorDeleteHelpTopic) {
			setLoading(false);
			CS.showErrorMessage(errorDeleteHelpTopic.response?.data?.message);
		}
	}, [errorDeleteHelpTopic]);

	const closeModal = () => {
		setHelpTopic({});
		setOpen(false);
		refetch();
	};

	const handleChangeTab = (tabFor) => {
		setTabChangeCounter((count) => count + 1);
		setTopicFor(tabFor);
		setHelpTopicsPayload({
			pagination: true,
			items_per_page: itemsPerPage,
			page: pageNow,
			filter: { ["help_topics.role_id"]: tabFor },
			search: "",
			role_id: tabFor,
		});
	};

	const columns = [
		{
			label: "Topic",
			type: "string",
			sort: true,
			path: "name",
			alias: "help_topics.name",
			content: (item) => (
				<a
					href="#"
					onClick={() => {
						setOpen(true);
						setHelpTopic(item);
					}}
				>
					{item.name}
				</a>
			),
		},
		{
			label: "Created On",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "help_topics.created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at),
		},
		{
			label: "Status",
			type: "boolean",
			aliasFilter: "help_topics.is_active",
			filterOptions: [
				{ value: 1, label: "Active" },
				{ value: 0, label: "Inactive" },
			],
			content: (item) => (
				<SingleStatusToggle
					_id={item.id}
					status={item.is_active}
					onToggleChange={() => {
						updateHelpTopic({
							id: item.id,
							is_active: item.is_active === 0 ? 1 : 0,
						});
						setLoading(true);
					}}
					htmlFor={item.id}
				/>
			),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="edit"
						data-tip
						data-for="edit"
						onClick={() => {
							setOpen(true);
							setHelpTopic(item);
						}}
					/>
					<ReactTooltip id="edit" place="top" effect="solid">
						Edit
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						onClick={() =>
							swal
								.fire({
									title: "Are you sure you want to delete?",
									icon: "warning",
									showCancelButton: true,
									confirmButtonColor: "#3085d6",
									cancelButtonColor: "#d33",
									confirmButtonText: "Yes, delete it!",
								})
								.then((result) => {
									if (result.isConfirmed) {
										deleteHelpTopic(item.id);
										setLoading(true);
									}
								})
						}
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			<BreadCrumbs
				title="Help Management"
				data={[{ label: "Help" }, { label: "Help Topics" }]}
			/>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="left-buttons d-flex border-bottom">
								<button
									style={{ marginRight: "10px" }}
									className={
										topicFor == ROLES_CONFIG.RESTAURANT
											? "btn glow-primary btn-primary"
											: "btn btn-default minW-md-0 btn-bg-white"
									}
									onClick={() => handleChangeTab(ROLES_CONFIG.RESTAURANT)}
								>
									Restaurant
								</button>
								<button
									className={
										topicFor == ROLES_CONFIG.USER
											? "btn glow-primary btn-primary"
											: "btn btn-default minW-md-0 btn-bg-white"
									}
									onClick={() => handleChangeTab(ROLES_CONFIG.USER)}
								>
									User
								</button>
							</div>
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												search={(value) => {
													setPageNow(1);
													setHelpTopicsPayload({
														...helpTopicsPayload,
														search: {
															"help_topics.name":
																!value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end">
											<button
												onClick={() => setOpen(true)}
												id="addNewHelp"
												type="button"
												className="btn glow-primary btn-primary"
											>
												<i className="bx bx-user-plus d-lg-none" />
												<span className="d-none d-sm-none d-lg-inline-block">
													Add Help Topic
												</span>
											</button>
										</div>
									</div>
								</div>
							</div>
							<Modal open={open} onCloseModal={closeModal}>
								<AddUpdate
									columnKey={columnKey}
									helpTopic={helpTopic}
									onCloseModal={closeModal}
									role_id={topicFor}
								/>
							</Modal>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								sortCol={(obj) => {
									setPageNow(1);
									setHelpTopicsPayload({
										...helpTopicsPayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setHelpTopicsPayload({ ...helpTopicsPayload, page });
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setHelpTopicsPayload({
										...helpTopicsPayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(
											helpTopicsPayload.filter,
											obj
										);
										setPageNow(1);
										setHelpTopicsPayload({
											...helpTopicsPayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setHelpTopicsPayload({
											...helpTopicsPayload,
											filter: {
												...helpTopicsPayload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setHelpTopicsPayload({
										...helpTopicsPayload,
										filter_date: dates && {
											...helpTopicsPayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
								tabChangeCounter={tabChangeCounter}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default HelpTopics;
